import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, NgZone, ChangeDetectorRef } from '@angular/core';
import { AppService } from '../../app.service';
import { AuthService } from '../../shared/core/index';
import { LookupList } from '../../shared/lookup';
import { DashboardService, DashboardCreateDto, DashboardUpdateDto, DashboardDto } from '../dashboard.service';
import { NotificationsService, Notification } from '../../shared/helper-components/index';

@Component({
  selector: 'dashboard-settings',
  standalone: false,
  templateUrl: './dashboard-settings.component.html',
  styleUrls: ['./dashboard-settings.component.css']
})
export class DashboardSettingsComponent implements OnInit, OnChanges {

  @Input() visible: boolean = false;
  @Input() projectId: string = '';
  @Output() visibleChange = new EventEmitter<boolean>();
  // Emits the id of the dashboard that should become active after the change
  // (the newly created one). Emits null when there is no specific target.
  @Output() dashboardsChanged = new EventEmitter<number>();

  editingDashboard: any = { id: '', name: '', isDefault: false };
  selectedCards: { type: string; value: string }[] = [];
  activeTab: 'select' | 'order' = 'select';

  dragIndex: number = -1;
  dragOverIndex: number = -1;

  dashboards: DashboardDto[] = [];
  selectDisabled: boolean = false;
  isLoading: boolean = false;
  private departmentDashboard: DashboardDto = null;

  statusOptions: string[] = [];
  tagOptions: string[] = [];
  restrictionOptions: string[] = [];
  othersOptions = [
    'All',
    'PENDING - All Pending Items',
    'Chat',
    'Urgent/Escalated',
    'Status Change'
  ];

  statusSelectOptions: string[] = ['Select All'];
  tagSelectOptions: string[] = ['Select All'];
  restrictionSelectOptions: string[] = ['Select All'];
  othersSelectOptions: string[] = [
    'Select All',
    'All',
    'PENDING - All Pending Items',
    'Chat',
    'Urgent/Escalated',
    'Status Change'
  ];

  statusSelected: string[] = [];
  tagSelected: string[] = [];
  restrictionSelected: string[] = [];
  othersSelected: string[] = [];

  statusMultiSelect = { allOptionChecked: false };
  tagsMultiSelect = { allOptionChecked: false };
  restrictionsMultiSelect = { allOptionChecked: false };
  othersMultiSelect = { allOptionChecked: false };

  isSaving: boolean = false;
  private selectUpdateLock: boolean = false;

  constructor(
    private _appService: AppService,
    private _authService: AuthService,
    private _dashboardService: DashboardService,
    private _notificationsService: NotificationsService,
    private ngZone: NgZone,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.loadDashboards();
      this.loadCILookups();
    }
    if (changes['visible'] && this.visible) {
      // Clear any previous data so a freshly opened panel never shows stale
      // or cached state while the new data loads.
      this.resetState();
      this.isLoading = true;
      this.loadDashboards(() => {
        this.resolveInitialForm();
        this.isLoading = false;
      });
    }
  }

  // Clears all in-memory state so opening the panel always starts fresh.
  private resetState(): void {
    this.dashboards = [];
    this.departmentDashboard = null;
    this.selectDisabled = false;
    this.editingDashboard = { id: '', name: '', isDefault: false };
    this.selectedCards = [];
    this.activeTab = 'select';
    this.statusMultiSelect.allOptionChecked = false;
    this.tagsMultiSelect.allOptionChecked = false;
    this.restrictionsMultiSelect.allOptionChecked = false;
    this.othersMultiSelect.allOptionChecked = false;
    this.syncSelectModels();
  }

  loadDashboards(onLoaded?: () => void): void {
    this._dashboardService.getDashboards(this.projectId)
      .subscribe(
        (dashboards) => {
          this.dashboards = dashboards || [];
          this.updateSelectDisabledState();
          // The default department dashboard is not returned by the user
          // project dashboards endpoint, so fetch it separately. It is used to
          // seed new dashboards.
          this.loadDepartmentDashboard(onLoaded);
        },
        (error) => {
          this.ngZone.run(() => {
            console.log('DashboardSettingsComponent.loadDashboards() >> error: ' + error);
            if (onLoaded) {
              onLoaded();
            }
            this.cd.detectChanges();
          });
        }
      );
  }

  // Loads the default department dashboard for this project. It comes from the
  // "all project dashboards" endpoint, which includes the department default in
  // addition to the user's own project dashboards.
  private loadDepartmentDashboard(onLoaded?: () => void): void {
    this._dashboardService.getAllProjectDashboards(this.projectId)
      .subscribe(
        (dashboards) => {
          // HTTP callbacks run outside the Angular zone in this app, so run the
          // state updates inside the zone and trigger change detection.
          this.ngZone.run(() => {
            const all = dashboards || [];
            this.departmentDashboard =
              all.find(d => d.Type === 'Department' && d.Default) ||
              all.find(d => d.Type === 'Department') ||
              null;
            if (onLoaded) {
              onLoaded();
            }
            this.cd.detectChanges();
          });
        },
        (error) => {
          this.ngZone.run(() => {
            console.log('DashboardSettingsComponent.loadDepartmentDashboard() >> error: ' + error);
            if (onLoaded) {
              onLoaded();
            }
            this.cd.detectChanges();
          });
        }
      );
  }

  // Dashboards created by the user (excludes the shared department dashboard).
  private get userDashboards(): DashboardDto[] {
    return this.dashboards.filter(d => d.Type !== 'Department');
  }

  private updateSelectDisabledState(): void {
    const savedCount = this.userDashboards.length;

    if (!this.editingDashboard.id) {
      // Create mode: keep the select enabled as long as there is at least one
      // saved dashboard, so the user can go back and edit an existing one.
      // Disable only when there is nothing to select at all.
      this.selectDisabled = savedCount === 0;
      return;
    }

    // Editing an existing dashboard: disable only when it is the single saved
    // dashboard (nothing else to switch to).
    this.selectDisabled = savedCount <= 1;
  }

  private resolveInitialForm(): void {
    // First time only (no saved user dashboards): drop straight into
    // "create a new dashboard" mode, seeded from the default department
    // dashboard. Otherwise open a saved dashboard by default (including the
    // single-dashboard case).
    if (this.userDashboards.length === 0) {
      this.startNewDashboard();
      return;
    }

    this.loadDefaultDashboardIntoForm();
  }

  // Shows a specific saved dashboard in the form after a create/update, so the
  // panel reflects what was just saved instead of resetting to create mode.
  private showSavedDashboard(id: number): void {
    const exists = id != null && this.dashboards.some(d => String(d.Id) === String(id));
    if (exists) {
      this.loadDashboardIntoForm(String(id));
    } else {
      this.resolveInitialForm();
    }
  }

  private loadDefaultDashboardIntoForm(): void {
    const userDashboards = this.userDashboards;
    if (userDashboards.length === 0) {
      return;
    }

    // Open one of the user's own dashboards by default (never the shared
    // department dashboard). Priority: default Project dashboard > first.
    let defaultDb = userDashboards.find(d => d.Default && d.Type === 'Project');
    if (!defaultDb) {
      defaultDb = userDashboards[0];
    }

    if (defaultDb) {
      this.loadDashboardIntoForm(String(defaultDb.Id));
    }
  }

  loadCILookups(): void {
    this._appService.retrieveCILookups('CIStatus,Tags,CIRestrictions', this.projectId, true)
      .subscribe(
        (list) => {
          if (list) {
            this.onLookupsRetrieved(list);
          }
        },
        (error) => {
          console.log('DashboardSettingsComponent.loadCILookups() >> error: ' + error);
        }
      );
  }

  onLookupsRetrieved(list: LookupList[]): void {
    if (!list || list.length === 0) {
      return;
    }

    const statusList = list.find(it => it.lookupType === 'CIStatus');
    if (statusList && statusList.lookupData) {
      this.statusOptions = statusList.lookupData.map(lookup => lookup.name);
      this.statusSelectOptions = ['Select All', ...this.statusOptions];
    }

    const tagsList = list.find(it => it.lookupType === 'Tags');
    if (tagsList && tagsList.lookupData) {
      this.tagOptions = tagsList.lookupData.map(lookup => lookup.name);
      this.tagSelectOptions = ['Select All', ...this.tagOptions];

      this.selectedCards = this.selectedCards.map(c =>
        c.type === 'Tag' ? { type: 'Tag', value: this.resolveTagOption(c.value) } : c);
    }

    const restrictionsList = list.find(it => it.lookupType === 'CIRestrictions');
    if (restrictionsList && restrictionsList.lookupData) {
      this.restrictionOptions = restrictionsList.lookupData.map(lookup => lookup.name);
      this.restrictionSelectOptions = ['Select All', ...this.restrictionOptions];
    }

    this.syncSelectModels();
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  loadDashboardIntoForm(id: string): void {
    const db = this.dashboards.find(d => String(d.Id) === String(id));
    if (!db) {
      return;
    }
    this.editingDashboard = { id: db.Id, name: db.Name, isDefault: db.Default };
    this.selectedCards = (db.Cards || []).map(c => this.reverseMapCard(c));
    this.syncAllOptionFlags();
    this.syncSelectModels();
    this.updateSelectDisabledState();
  }

  // Enter "create new dashboard" mode. Seed the cards from the default
  // department dashboard so the user can add/remove options instead of
  // starting from a blank slate.
  startNewDashboard(): void {
    this.editingDashboard = { id: '', name: '', isDefault: false };
    this.activeTab = 'select';
    this.statusMultiSelect.allOptionChecked = false;
    this.tagsMultiSelect.allOptionChecked = false;
    this.restrictionsMultiSelect.allOptionChecked = false;
    this.othersMultiSelect.allOptionChecked = false;

    this.selectedCards = this.departmentDashboard
      ? (this.departmentDashboard.Cards || []).map(c => this.reverseMapCard(c))
      : [];

    this.syncAllOptionFlags();
    this.syncSelectModels();
    this.updateSelectDisabledState();
  }

  clearForm(): void {
    this.startNewDashboard();
  }

  isCardSelected(type: string, value: string): boolean {
    return this.selectedCards.some(c => c.type === type && c.value === value);
  }

  toggleCard(type: string, value: string): void {
    const idx = this.selectedCards.findIndex(c => c.type === type && c.value === value);
    if (idx >= 0) {
      this.selectedCards.splice(idx, 1);
    } else {
      this.selectedCards.push({ type, value });
    }
  }

  onMultiSelectChange(type: string, selected: string[], multiSelect: { allOptionChecked: boolean }): void {
    if (this.selectUpdateLock) {
      return;
    }
    this.selectUpdateLock = true;
    try {
      const selectAllChecked = selected.indexOf('Select All') >= 0;

      if (selectAllChecked && !multiSelect.allOptionChecked) {
        this.checkAllOptions(type, multiSelect);
        this.setTypeSelected(type, ['Select All', ...this.getOptionsForType(type)]);
        return;
      }

      if (!selectAllChecked && multiSelect.allOptionChecked) {
        this.uncheckAllOptions(type, multiSelect);
        this.setTypeSelected(type, []);
        return;
      }

      if (selectAllChecked && multiSelect.allOptionChecked) {
        return;
      }

      const values = selected.filter(val => val !== 'Select All');
      this.setMultiSelectValue(type, values);
      this.setTypeSelected(type, values);
    } finally {
      this.selectUpdateLock = false;
    }
  }

  private checkAllOptions(type: string, multiSelect: { allOptionChecked: boolean }): void {
    multiSelect.allOptionChecked = true;
    this.selectedCards = this.selectedCards.filter(c => c.type !== type);
    this.getOptionsForType(type).forEach(val => this.selectedCards.push({ type, value: val }));
  }

  private uncheckAllOptions(type: string, multiSelect: { allOptionChecked: boolean }): void {
    multiSelect.allOptionChecked = false;
    this.selectedCards = this.selectedCards.filter(c => c.type !== type);
  }

  private getOptionsForType(type: string): string[] {
    if (type === 'Status') {
      return this.statusOptions;
    }
    if (type === 'Tag') {
      return this.tagOptions;
    }
    if (type === 'Restriction') {
      return this.restrictionOptions;
    }
    if (type === 'Others') {
      return this.othersOptions;
    }
    return [];
  }

  private getMultiSelect(type: string): { allOptionChecked: boolean } {
    if (type === 'Status') {
      return this.statusMultiSelect;
    }
    if (type === 'Tag') {
      return this.tagsMultiSelect;
    }
    if (type === 'Restriction') {
      return this.restrictionsMultiSelect;
    }
    if (type === 'Others') {
      return this.othersMultiSelect;
    }
    return this.statusMultiSelect;
  }

  private setMultiSelectValue(type: string, values: string[]): void {
    this.selectedCards = this.selectedCards.filter(c => c.type !== type);
    values.forEach(val => this.selectedCards.push({ type, value: val }));
  }

  private syncAllOptionFlags(): void {
    this.statusMultiSelect.allOptionChecked = this.areAllSelected('Status');
    this.tagsMultiSelect.allOptionChecked = this.areAllSelected('Tag');
    this.restrictionsMultiSelect.allOptionChecked = this.areAllSelected('Restriction');
    this.othersMultiSelect.allOptionChecked = this.areAllSelected('Others');
  }

  private areAllSelected(type: string): boolean {
    const options = this.getOptionsForType(type);
    if (options.length === 0) {
      return false;
    }
    return options.every(opt => this.isCardSelected(type, opt));
  }

  private syncSelectModels(): void {
    this.setTypeSelected('Status', this.buildSelectedValues('Status'));
    this.setTypeSelected('Tag', this.buildSelectedValues('Tag'));
    this.setTypeSelected('Restriction', this.buildSelectedValues('Restriction'));
    this.setTypeSelected('Others', this.buildSelectedValues('Others'));
  }

  private buildSelectedValues(type: string): string[] {
    const values = this.selectedCards.filter(c => c.type === type).map(c => c.value);
    const multiSelect = this.getMultiSelect(type);
    if (multiSelect && multiSelect.allOptionChecked) {
      return ['Select All', ...values];
    }
    return values.slice();
  }

  private setTypeSelected(type: string, values: string[]): void {
    if (type === 'Status') {
      this.statusSelected = values;
      return;
    }
    if (type === 'Tag') {
      this.tagSelected = values;
      return;
    }
    if (type === 'Restriction') {
      this.restrictionSelected = values;
      return;
    }
    if (type === 'Others') {
      this.othersSelected = values;
    }
  }

  private tagLabelAliases: { [key: string]: string } = {
    'ART': 'Art Department'
  };

  private reverseMapCard(card: { Type: string; Label: string }): { type: string; value: string } {
    switch (card.Type) {
      case 'Status':
      case 'SecondStatus':
        return { type: 'Status', value: card.Label };
      case 'Tag':
        return { type: 'Tag', value: this.resolveTagOption(card.Label) };
      case 'Restriction':
        return { type: 'Restriction', value: card.Label };
      case 'All':
        return { type: 'Others', value: 'All' };
      case 'AllPending':
        return { type: 'Others', value: 'PENDING - All Pending Items' };
      case 'Chat':
        return { type: 'Others', value: 'Chat' };
      case 'Urgent':
        return { type: 'Others', value: 'Urgent/Escalated' };
      case 'StatusChange':
        return { type: 'Others', value: 'Status Change' };
      default:
        return { type: card.Type, value: card.Label };
    }
  }

  private resolveTagOption(label: string): string {
    if (!label) {
      return label;
    }
    if (this.tagOptions.indexOf(label) >= 0) {
      return label;
    }
    const alias = this.tagLabelAliases[label];
    if (alias && this.tagOptions.indexOf(alias) >= 0) {
      return alias;
    }
    const match = this.tagOptions.find(opt => opt.toUpperCase() === label.toUpperCase());
    return match || label;
  }

  removeCard(index: number): void {
    this.selectedCards.splice(index, 1);
    this.syncAllOptionFlags();
    this.syncSelectModels();
  }

  cardLabel(card: { type: string; value: string }): string {
    return card.value ? `${card.type}: ${card.value}` : card.type;
  }

  displaySelected(selected: string[]): string {
    if (!selected || selected.length === 0) {
      return '';
    }
    return selected.filter(value => value !== 'Select All').join(', ');
  }

  onDragStart(index: number): void {
    this.dragIndex = index;
  }

  onDragOver(event: DragEvent, index: number): void {
    event.preventDefault();
    this.dragOverIndex = index;
  }

  onDrop(index: number): void {
    if (this.dragIndex === -1 || this.dragIndex === index) {
      this.dragIndex = -1;
      this.dragOverIndex = -1;
      return;
    }
    const moved = this.selectedCards.splice(this.dragIndex, 1)[0];
    this.selectedCards.splice(index, 0, moved);
    this.dragIndex = -1;
    this.dragOverIndex = -1;
  }

  onDragEnd(): void {
    this.dragIndex = -1;
    this.dragOverIndex = -1;
  }

  saveDashboard(): void {
    if (!this.editingDashboard.name || !this.editingDashboard.name.trim()) {
      return;
    }
    if (this.selectedCards.length === 0) {
      return;
    }

    this.isSaving = true;
    if (this.editingDashboard.id) {
      this.updateDashboard();
    } else {
      this.createDashboard();
    }
  }

  private createDashboard(): void {
    const dto: DashboardCreateDto = {
      Name: this.editingDashboard.name,
      Type: 'Project',
      Department: '',
      SFProjectId: this.projectId,
      UserId: this._authService.userInfo.userId,
      Default: this.editingDashboard.isDefault,
      Cards: this.selectedCards.map(c => ({
        Label: c.value || c.type,
        Type: this.mapCardType(c)
      }))
    };

    this._dashboardService.createDashboard(dto)
      .subscribe(
        (response) => {
          const newId = this.extractDashboardId(response);
          this.ngZone.run(() => {
            this.isSaving = false;
            this._notificationsService.add(
              new Notification('success', 'Dashboard created successfully'));
            // Reload the settings list, then show the dashboard just created in
            // the form (instead of dropping back into "create new" mode).
            this.loadDashboards(() => this.showSavedDashboard(newId));
            // Tell the parent to reload and select the dashboard just created
            // so it shows immediately without needing a page refresh.
            this.dashboardsChanged.emit(newId);
            this.cd.detectChanges();
          });
        },
        (error) => {
          this.ngZone.run(() => {
            this.isSaving = false;
            this._notificationsService.add(
              new Notification('error', 'Failed to create dashboard. Please try again.'));
            console.log('DashboardSettingsComponent.createDashboard() >> error: ' + error);
            this.cd.detectChanges();
          });
        }
      );
  }

  private updateDashboard(): void {
    const updatedId = this.editingDashboard.id ? Number(this.editingDashboard.id) : null;
    const dto: DashboardUpdateDto = {
      Name: this.editingDashboard.name,
      Default: this.editingDashboard.isDefault,
      Cards: this.selectedCards.map(c => ({
        Id: null,
        Label: c.value || c.type,
        Type: this.mapCardType(c)
      }))
    };

    this._dashboardService.updateDashboard(this.editingDashboard.id, dto)
      .subscribe(
        () => {
          this.ngZone.run(() => {
            this.isSaving = false;
            this._notificationsService.add(
              new Notification('success', 'Dashboard updated successfully'));
            // Reload the settings list, then keep showing the edited dashboard.
            this.loadDashboards(() => this.showSavedDashboard(updatedId));
            this.dashboardsChanged.emit(updatedId);
            this.cd.detectChanges();
          });
        },
        (error) => {
          this.ngZone.run(() => {
            this.isSaving = false;
            this._notificationsService.add(
              new Notification('error', 'Failed to update dashboard. Please try again.'));
            console.log('DashboardSettingsComponent.updateDashboard() >> error: ' + error);
            this.cd.detectChanges();
          });
        }
      );
  }

  deleteDashboard(): void {
    if (!this.editingDashboard.id) {
      return;
    }

    if (!confirm('Are you sure you want to delete this dashboard?')) {
      return;
    }

    this._dashboardService.deleteDashboard(this.editingDashboard.id)
      .subscribe(
        () => {
          this.ngZone.run(() => {
            this.loadDashboards();
            this.clearForm();
            this._notificationsService.add(
              new Notification('success', 'Dashboard deleted successfully'));
            this.dashboardsChanged.emit(null);
            this.cd.detectChanges();
          });
        },
        (error) => {
          this.ngZone.run(() => {
            this._notificationsService.add(
              new Notification('error', 'Failed to delete dashboard. Please try again.'));
            console.log('DashboardSettingsComponent.deleteDashboard() >> error: ' + error);
            this.cd.detectChanges();
          });
        }
      );
  }

  private mapCardType(card: { type: string; value: string }): string {
    if (card.type === 'Status') {
      return this._authService.secondApproval ? 'SecondStatus' : 'Status';
    }
    if (card.type === 'Tag') {
      return 'Tag';
    }
    if (card.type === 'Restriction') {
      return 'Restriction';
    }
    if (card.type === 'Others') {
      if (card.value === 'All') {
        return 'All';
      }
      if (card.value === 'PENDING - All Pending Items') {
        return 'AllPending';
      }
      if (card.value === 'Chat') {
        return 'Chat';
      }
      if (card.value === 'Urgent/Escalated') {
        return 'Urgent';
      }
      if (card.value === 'Status Change') {
        return 'StatusChange';
      }
    }
    return card.type;
  }

  // Reads the new dashboard id from the create response. The backend returns
  // the created DashboardDto; httpPost may deliver it as an object or as a JSON
  // string, so handle both.
  private extractDashboardId(response: any): number {
    if (response == null) {
      return null;
    }
    let data = response;
    if (typeof response === 'string') {
      try {
        data = JSON.parse(response);
      } catch (e) {
        return null;
      }
    }
    return data && data.Id != null ? Number(data.Id) : null;
  }
}
